import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import './GroupDetailModal.css';

function GroupDetailModal({ group, tables, onUpdate, onDissolve, onClose }) {
  const { t } = useTranslation();
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(group.name);
  const [addTableNumber, setAddTableNumber] = useState('');
  const [loading, setLoading] = useState(false);

  const groupTables = useMemo(
    () => tables.filter((tb) => group.tableNumbers.includes(String(tb.tableNumber))),
    [tables, group.tableNumbers]
  );

  const availableTables = useMemo(
    () => tables.filter((tb) => !tb.groupId && !tb.currentSessionId),
    [tables]
  );

  const handleNameSave = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === group.name) {
      setEditingName(false);
      setNameValue(group.name);
      return;
    }
    setLoading(true);
    try {
      await onUpdate(group.groupId, { name: trimmed });
    } finally {
      setLoading(false);
      setEditingName(false);
    }
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') handleNameSave();
    if (e.key === 'Escape') {
      setEditingName(false);
      setNameValue(group.name);
    }
  };

  const handleRemoveTable = async (tableNumber) => {
    const remaining = group.tableNumbers.filter((tn) => tn !== String(tableNumber));
    if (remaining.length < 2) {
      if (!window.confirm(t('tables.dissolveGroupConfirm'))) return;
      setLoading(true);
      try {
        await onDissolve(group.groupId);
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    try {
      await onUpdate(group.groupId, { removeTableNumbers: [String(tableNumber)] });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTable = async () => {
    if (!addTableNumber) return;
    setLoading(true);
    try {
      await onUpdate(group.groupId, { addTableNumbers: [addTableNumber] });
      setAddTableNumber('');
    } finally {
      setLoading(false);
    }
  };

  const handleDissolve = async () => {
    if (!window.confirm(t('tables.dissolveGroupConfirm'))) return;
    setLoading(true);
    try {
      await onDissolve(group.groupId);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="gdm-header">
            {editingName ? (
              <input
                className="gdm-name-input"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onBlur={handleNameSave}
                onKeyDown={handleNameKeyDown}
                autoFocus
                disabled={loading}
              />
            ) : (
              <span className="gdm-header-name" onClick={() => setEditingName(true)}>
                {group.name}
                <Pencil size={16} />
              </span>
            )}
          </div>
          <button type="button" className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          <div className="gdm-section-title">{t('tables.groupTables')}</div>
          <div className="gdm-table-chips">
            {groupTables.map((tb) => (
              <div key={tb.tableNumber} className="gdm-chip">
                {t('tables.table')} {tb.tableNumber}
                <button
                  type="button"
                  className="gdm-chip-remove"
                  onClick={() => handleRemoveTable(tb.tableNumber)}
                  disabled={loading}
                  title={
                    group.tableNumbers.length <= 2
                      ? t('tables.removeLastTableWarning')
                      : t('tables.removeFromGroup')
                  }
                >
                  &times;
                </button>
              </div>
            ))}
          </div>

          <div className="gdm-section-title">{t('tables.addTableToGroup')}</div>
          <div className="gdm-add-section">
            {availableTables.length > 0 ? (
              <div className="gdm-add-row">
                <select
                  className="gdm-add-select"
                  value={addTableNumber}
                  onChange={(e) => setAddTableNumber(e.target.value)}
                  disabled={loading}
                >
                  <option value="">{t('tables.addTableToGroup')}...</option>
                  {availableTables.map((tb) => (
                    <option key={tb.tableNumber} value={String(tb.tableNumber)}>
                      {t('tables.table')} {tb.tableNumber}
                      {tb.location ? ` (${tb.location})` : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleAddTable}
                  disabled={!addTableNumber || loading}
                >
                  +
                </button>
              </div>
            ) : (
              <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
                {t('tables.noAvailableTables')}
              </span>
            )}
          </div>
        </div>

        <div className="modal-actions gdm-actions">
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={handleDissolve}
            disabled={loading}
          >
            {t('tables.dissolveGroup')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GroupDetailModal;
